import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { motion } from "framer-motion";
import { Mail, Lock, User, ArrowRight, Eye, EyeOff } from "lucide-react";
import { api } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/SEO";

export default function SignupPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      return toast.error("Name, email, and password are required");
    }
    if (!agreedToTerms) {
      return toast.error("You must agree to the Terms of Service and Privacy Policy");
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", {
        name,
        email,
        password,
      });
      if (!data.token || !data.user) {
        console.error("Invalid signup response:", data);
        toast.error("Invalid response from server");
        return;
      }
      login(data.token, data.user);
      toast.success("Welcome to Bill Forge!");
      
      // Check for redirect parameter
      const redirectPath = new URLSearchParams(location.search).get('redirect');
      nav(redirectPath || '/app/onboarding');
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Sign Up"
        description="Create your Bill Forge account. Start your free trial today."
        path="/signup"
        noindex={true}
      />
      <div className="min-h-screen flex items-center justify-center p-6 sm:p-12 bg-white dark:bg-[#0A0A0B] text-slate-900 dark:text-slate-100">
        <Toaster position="top-right" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="uppercase text-xs tracking-[0.3em] text-slate-500 mb-3">Get Started</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tighter mb-2">
            Create account.
          </h1>
          <p className="text-slate-500 mb-10">Start your free trial today. No credit card required.</p>

            <form onSubmit={handleSignup} className="space-y-5">
              <label className="block">
                <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Name</span>
                <div className="mt-2 flex items-center gap-3 border border-slate-300 dark:border-slate-700 rounded-sm px-4 py-3 focus-within:border-slate-900 dark:focus-within:border-white transition-colors">
                  <User className="w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full bg-transparent outline-none text-lg"
                    required
                  />
                </div>
              </label>

              <label className="block">
                <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Email</span>
                <div className="mt-2 flex items-center gap-3 border border-slate-300 dark:border-slate-700 rounded-sm px-4 py-3 focus-within:border-slate-900 dark:focus-within:border-white transition-colors">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full bg-transparent outline-none text-lg"
                    required
                  />
                </div>
              </label>

              <label className="block">
                <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Password</span>
                <div className="mt-2 flex items-center gap-3 border border-slate-300 dark:border-slate-700 rounded-sm px-4 py-3 focus-within:border-slate-900 dark:focus-within:border-white transition-colors">
                  <Lock className="w-4 h-4 text-slate-500" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-transparent outline-none text-lg"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-slate-300 dark:border-slate-700 text-primary focus:ring-primary"
                  required
                />
                <span className="text-sm text-slate-500">
                  I agree to the{" "}
                  <Link to="/terms" className="text-primary hover:underline">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-primary hover:underline">
                    Privacy Policy
                  </Link>
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-4 rounded-sm font-semibold hover:bg-[#002FA7] hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? "Creating account..." : "Create Account"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>

            <div className="mt-6 text-center">
              <span className="text-sm text-slate-500">
                Already have an account?{" "}
                <Link to="/login" className="text-primary hover:underline">
                  Login
                </Link>
              </span>
            </div>
          </motion.div>
        </div>
      </>
  );
}
